import { useAuthStore } from '@/store/authStore'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Sparkles, FileText, GraduationCap, Projector, AlertCircle } from 'lucide-react'

export function ASD() {
  const { partner } = useAuthStore()
  
  // Only Premium/Elite partners should see this page
  if (partner?.tier !== 'Premium' && partner?.tier !== 'Elite') {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <h2 className="mt-4 text-xl font-semibold">Premium Feature</h2>
        <p className="mt-2 text-center text-muted-foreground max-w-md">
          Advanced Services Delivery (ASD) program access is available to Premium and Elite tier partners only.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Advanced Services Delivery</h1>
          <Badge className="bg-gold text-black">{partner?.tier} Tier</Badge>
        </div>
        <p className="text-muted-foreground">
          ASD program details, qualification criteria, and project resources
        </p>
      </div>

      {/* ASD Overview */}
      <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">ASD Program Overview</h3>
              <p className="mt-1 text-muted-foreground">
                The Advanced Services Delivery program recognizes partners who deliver 
                complex, high-value Cloudflare implementations. As a {partner?.tier} tier partner, 
                you have access to exclusive ASD resources and project opportunities.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resource Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
              <GraduationCap className="h-6 w-6 text-amber-500" />
            </div>
            <CardTitle>Qualification Criteria</CardTitle>
            <CardDescription>
              Requirements for ASD certification
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Learn about the technical and business requirements to become an 
              ASD-certified partner and deliver advanced Cloudflare services.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
              <Projector className="h-6 w-6 text-amber-500" />
            </div>
            <CardTitle>Project Templates</CardTitle>
            <CardDescription>
              Standardized project methodologies
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Access to project templates, statement of work examples, and 
              implementation frameworks for complex deployments.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
              <FileText className="h-6 w-6 text-amber-500" />
            </div>
            <CardTitle>SOW Examples</CardTitle>
            <CardDescription>
              Statement of Work templates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Pre-built SOW templates for common advanced services engagements 
              including pricing guidelines and deliverable structures.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Coming Soon */}
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center p-12 text-center">
          <Sparkles className="h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">ASD Resources in Development</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-md">
            We're building out comprehensive ASD resources including case studies, 
            project playbooks, and certification paths. Check back soon!
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
